import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFormContext, useWatch } from 'react-hook-form';
import styles from './box-send-file.module.scss';
import { usePostUploadCnh } from '@/_features/motoboys/api/post-upload-cnh';
import { usePostUploadCrlv } from '@/_features/motoboys/api/post-upload-crlv';
import { usePostUploadRg } from '@/_features/motoboys/api/post-upload-rg';
import { ENUM_FILE_NAME } from '../../types';
import { RowSendFile } from '../row-send-file';
import useMotoboy from '@/hooks/motoboy/useMotoboy';
import { VEHICLE_TYPES } from '../../constants';

function BoxSendFile() {
  const { motoboyId } = useParams<{ motoboyId: string }>();
  const [loadingFrontRg, setLoadingFrontRg] = useState(false);
  const { control } = useFormContext();

  const typeVehicle = useWatch({ control, name: 'tipoVeiculo' });

  const { motoboy } = useMotoboy(motoboyId || '');

  const {
    deliverymanCnhPictureKey,
    deliverymanCrlvPictureKey,
    deliverymanFrontRgPictureKey,
    deliverymanBackRgPictureKey,
  } = motoboy || {};

  const { rg, cnh, crlv } = motoboy?.preRegistration || {};

  const { mutate: mutateCnh, isLoading: isLoadingCnh } = usePostUploadCnh();
  const { mutate: mutateCrlv, isLoading: isLoadingCrlv } = usePostUploadCrlv();
  const { mutate: mutateRg, isLoading: isLoadingRg } = usePostUploadRg({
    onSettled: () => {
      setLoadingFrontRg(false);
    },
  });

  function checkIsUrl(key: string | undefined) {
    if (typeof key !== 'string') return false;
    return key?.includes('https');
  }

  if (!motoboyId) return;

  const needDocumentsToDrive =
    typeVehicle !== undefined && typeVehicle !== VEHICLE_TYPES.BICICLETA;

  const onSave = ({
    file,
    fileName,
  }: {
    file: File;
    fileName: ENUM_FILE_NAME;
  }) => {
    if (fileName === ENUM_FILE_NAME.CNH_FILE) {
      const formData = new FormData();
      formData.append('saveDeliverymanCnhPictureByManager', file);
      mutateCnh({
        deliverymanId: motoboyId,
        file: formData,
      });
    }

    if (fileName === ENUM_FILE_NAME.CRLV_FILE) {
      const formData = new FormData();
      formData.append('saveDeliverymanCrlvPictureByManager', file);
      mutateCrlv({
        deliverymanId: motoboyId,
        file: formData,
      });
    }

    if (fileName === ENUM_FILE_NAME.RG_FRONT_FILE) {
      setLoadingFrontRg(true);
      const formData = new FormData();
      formData.append('saveDeliverymanRgPictureByManager', file);
      mutateRg({
        deliverymanId: motoboyId,
        file: formData,
        sideRg: 'RG_FRONT',
      });
    }

    if (fileName === ENUM_FILE_NAME.RG_BACK_FILE) {
      const formData = new FormData();
      formData.append('saveDeliverymanRgPictureByManager', file);
      mutateRg({
        deliverymanId: motoboyId,
        file: formData,
        sideRg: 'RG_BACK',
      });
    }
  };

  return (
    <div className={styles.container}>
      <span className={styles.subtitleForm}>Anexar os documentos</span>

      <div className={styles.wrapperDocs}>
        <RowSendFile
          title="Documento de identidade (RG)"
          fileName={ENUM_FILE_NAME.RG_FRONT_FILE}
          image={
            checkIsUrl(deliverymanFrontRgPictureKey)
              ? deliverymanFrontRgPictureKey
              : rg?.imageFrontId
          }
          isLoadingButton={isLoadingRg && loadingFrontRg}
          onSave={onSave}
        />

        <RowSendFile
          title="Documento de identidade (RG) verso (se houver)"
          fileName={ENUM_FILE_NAME.RG_BACK_FILE}
          image={
            checkIsUrl(deliverymanBackRgPictureKey)
              ? deliverymanBackRgPictureKey
              : rg?.imageBackId
          }
          isLoadingButton={isLoadingRg && !loadingFrontRg}
          onSave={onSave}
        />

        {needDocumentsToDrive ? (
          <RowSendFile
            title="Carteira de Habilitação (CNH)"
            fileName={ENUM_FILE_NAME.CNH_FILE}
            image={
              checkIsUrl(deliverymanCnhPictureKey)
                ? deliverymanCnhPictureKey
                : cnh?.imageId
            }
            isLoadingButton={isLoadingCnh}
            onSave={onSave}
          />
        ) : null}

        {needDocumentsToDrive ? (
          <RowSendFile
            title="Certificado de Registro e Licenciamento de Veículo (CRLV)"
            fileName={ENUM_FILE_NAME.CRLV_FILE}
            image={
              checkIsUrl(deliverymanCrlvPictureKey)
                ? deliverymanCrlvPictureKey
                : crlv?.imageId
            }
            isLoadingButton={isLoadingCrlv}
            onSave={onSave}
          />
        ) : null}
      </div>
    </div>
  );
}

export { BoxSendFile };
